import { array } from '@mojotech/json-type-validation'

import { CategoryRatingData } from '@/domain/entities/dashboard/categoryRatingData'
import { DailySoluition } from '@/domain/entities/dashboard/dailySolution'
import { WeeklyRating } from '@/domain/entities/dashboard/weeklyRating'
import { JudgeRecord } from '@/domain/entities/judge/judgeRecord'
import { Course } from '@/domain/entities/problem/course'
import { CourseSolutionRecord } from '@/domain/entities/problem/courseSolutionRecord'
import { LanguageStatistics } from '@/domain/entities/problem/languageStatistics'
import { User } from '@/domain/entities/user/user'
import { DashboardRepository } from '@/domain/repositories/dashboard/DashboardRepository'
import { ProgramLanguageType } from '@/domain/value_objects/language/programLanguageType'
import { ApiClients } from '@/infrastructure/clients/apiClients'
import { judgeRecordDecoder } from '@/infrastructure/decoders/judge/judgeRecordDecoder'
import { userDecoder } from '@/infrastructure/decoders/user/userDecoder'

export class DashboardRepositoryImpl implements DashboardRepository {
  constructor(private clients: ApiClients) {}

  async isExistUser(userId: string): Promise<User> {
    const path = `users/${userId}`
    const data = await this.clients.aojClient.fetchAndDecode(userDecoder, path)
    return data
  }

  async getUserStatistics(userId: string): Promise<LanguageStatistics> {
    const path = `statistics/users/${userId}/overview`
    const data = (await this.clients.analyticClient.fetch(path)) as any
    const statistics = data.languageStatistics.languages

    const languageStatistics: LanguageStatistics = {
      language: [],
      value: []
    }

    for (const val in statistics) {
      languageStatistics.language.push(val)
      languageStatistics.value.push(statistics[val])
    }

    return languageStatistics
  }

  async getDailySolutionList(userId: string): Promise<DailySoluition[]> {
    const path = `/users/${userId}/dailyachievement`
    const data = (await this.clients.analyticClient.fetch(path)) as any
    return data.counters.ALL
  }

  async getWeeklyRatingList(userId: string): Promise<WeeklyRating[]> {
    const path = `rating/users/${userId}`
    const data = (await this.clients.analyticClient.fetch(path)) as any
    return data.weeklyRatings
  }

  async getUserCategoryRatingList(userId: string): Promise<CategoryRatingData> {
    const path = `scores/users/${userId}/category`
    const res = (await this.clients.analyticClient.fetch(path)) as any

    const data: CategoryRatingData = {
      whatCategoryRatingList: res.whatCategoryRatings,
      howCategoryRatingList: res.howCategoryRatings,
      programmingCategoryRatingList: res.programmingCategoryRatings
    }

    return data
  }

  async getUserCategoryRatingListByLanguage(
    userId: string,
    programLanguage: ProgramLanguageType | 'All'
  ): Promise<CategoryRatingData> {
    const path = `scores/users/${userId}/languages/${programLanguage}/category`
    const res = (await this.clients.analyticClient.fetch(path)) as any

    const data: CategoryRatingData = {
      whatCategoryRatingList: res.whatCategoryRatings,
      howCategoryRatingList: res.howCategoryRatings,
      programmingCategoryRatingList: res.programmingCategoryRatings
    }

    return data
  }

  async getUserJudgeRecordList(userId: string): Promise<JudgeRecord[]> {
    const path = `submission_records/users/${userId}?page=0&size=65536`
    const res = await this.clients.aojClient.fetchAndDecode(array(judgeRecordDecoder), path)
    return res
  }

  async getCourseStatisticsByLanguage(
    courseName: string,
    userId: string,
    programLanguage: ProgramLanguageType
  ): Promise<Course> {
    const newLanguage = programLanguage.replace(' (Beta)', '').replace('#', '%23')
    const path = `progress/courses/${courseName}/users/${userId}/languages/${newLanguage}`
    const data = (await this.clients.analyticClient.fetch(path)) as any
    return data.course
  }

  async getCourseSolutoinRecordList(userId: string): Promise<CourseSolutionRecord[]> {
    const path = `solution_records/users/${userId}`
    const res = (await this.clients.analyticClient.fetch(path)) as any
    const trimRecordList = res.solutionRecords.filter(
      (record: any) => !/^\d{4}$/.test(record.problemId) // チャレンジの問題は除外
    ) as CourseSolutionRecord[]
    return trimRecordList
  }
}
